import type { HealthGaugeViewData } from '@shared/types/views';
import { statusLabel } from '@/components/views/atoms/StatusBadge';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { CommunicationCue } from '@/components/views/atoms/CommunicationCue';
import { DecisionBand, MeterBar, ON_BAND_ACTION_CLASS, VIEW_TEXT } from '@/components/views/atoms/view-grammar';
import { formatDueDate } from '@/lib/format-due-date';
import { cn } from '@/lib/utils';

interface HealthGaugeViewProps {
  data: HealthGaugeViewData;
}

const ENTITY_LABEL: Record<HealthGaugeViewData['entity_type'], string> = {
  account: 'Compte',
  contact: 'Contact',
  deal: 'Opportunité',
};

/**
 * Health of an account, contact or deal: the decision band (score, state,
 * what to do), then each dimension on the one bar with its signals.
 */
export const HealthGaugeView = ({ data }: HealthGaugeViewProps) => {
  const max = data.overall_max ?? 10;
  const badge = `${data.overall_score}/${max} · ${statusLabel(data.overall_status)}`;
  const context = `${data.entity_name} · ${ENTITY_LABEL[data.entity_type]}`;
  const action = data.primaryAction
    ? <ViewActionButton action={data.primaryAction} className={ON_BAND_ACTION_CLASS} />
    : null;

  return (
    <div data-view-surface className="flex flex-col gap-6 rounded-2xl border border-domino-cardBorder bg-domino-warm p-6 lg:p-8">
      <DecisionBand badge={badge} context={context} action={action}>
        {data.recommended_action ?? `Santé du compte : ${statusLabel(data.overall_status).toLowerCase()}.`}
      </DecisionBand>

      <div className="border-t border-domino-border">
        {data.dimensions.map((dimension) => {
          const dimensionMax = dimension.max ?? 10;
          return (
            <div
              key={`${dimension.label}-${dimension.score}`}
              className="grid grid-cols-[104px_minmax(0,1fr)_56px] items-center gap-x-4 gap-y-1 border-b border-domino-border py-3"
            >
              <span className={cn('truncate', VIEW_TEXT.detail)} title={dimension.label}>{dimension.label}</span>
              <MeterBar value={dimension.score} max={dimensionMax} />
              <span className="text-right text-[15px] font-medium tabular-nums text-domino-ink">{dimension.score}/{dimensionMax}</span>
              {dimension.signals.length > 0 && (
                <span className={cn('col-start-2 col-end-4', VIEW_TEXT.detail)}>{dimension.signals.join(' · ')}</span>
              )}
            </div>
          );
        })}
      </div>

      {data.communication && <CommunicationCue cue={data.communication} />}
      <p className={VIEW_TEXT.detail}>Mis à jour le {formatDueDate(data.last_updated).label}</p>
    </div>
  );
};
